/**
 * VaultEditorPane — read-only rendering OR TipTap-driven editing of a
 * vault note.
 *
 * C.5 shipped read-only display; C.6 adds an optional TipTap edit mode
 * with the four vendored Obsidian extensions (WikilinkMark, EmbedBlock,
 * ObsidianTag, CalloutBlock). The mode is controlled by the `editing`
 * prop; the parent (ObsidianVaultWindow) flips it via the toolbar.
 *
 * Wikilink clicks call `onWikilink(target, modifier)` so the parent can
 * route to pane A or pane B per the Q28 split-on-click contract.
 *
 * Auto-save and SharePoint round-trip land in C.9. For C.6 the edit is
 * local-state only; an `onLocalEdit` callback fires with the serialized
 * markdown so the toolbar can show "Unsaved" status.
 */

import { Fragment, useMemo, useState } from 'react';
import { testIds } from '../../lib/testIds';
import type { ParsedNote } from '../../lib/vault/parse';
import { VaultTipTapEditor } from './VaultTipTapEditor';
import { RegisterAsSkillDialog } from './RegisterAsSkillDialog';

const SKILL_PATH_RE = /^Skills\/[^/]+\/SKILL\.md$/i;

interface SkillCandidate {
  name: string;
  description: string;
  body: string;
  vaultPath: string;
}

function matchSkillNote(note: ParsedNote): SkillCandidate | null {
  if (!SKILL_PATH_RE.test(note.path)) return null;
  const fm = note.frontmatter;
  if (
    typeof fm['name'] !== 'string' ||
    typeof fm['description'] !== 'string' ||
    !fm['name'] ||
    !fm['description']
  ) {
    return { name: '', description: '', body: note.raw ?? note.body, vaultPath: note.path };
  }
  return {
    name: fm['name'] as string,
    description: fm['description'] as string,
    body: note.raw ?? note.body,
    vaultPath: note.path,
  };
}

export interface VaultEditorPaneProps {
  note: ParsedNote | null;
  notesByName: Map<string, ParsedNote>;
  notesByPath: Map<string, ParsedNote>;
  paneId: 'a' | 'b';
  onWikilink: (target: string, modifier: 'pane-a' | 'pane-b') => void;
  /** When true, TipTap renders an editable view instead of the read-only renderer. */
  editing?: boolean;
  /** Fires on every local edit with the serialized markdown body. */
  onLocalEdit?: (markdown: string) => void;
}

const WIKILINK_TOKEN = /\[\[([^\]|#\n]+?)(#[^\]|\n]+)?(?:\|([^\]\n]+))?\]\]/g;
const EMBED_TOKEN = /!\[\[([^\]\n]+?)\]\]/g;
const TAG_TOKEN = /(?:^|\s)#([A-Za-z0-9_/-]+)/g;
const CALLOUT_START = /^>\s*\[!([A-Za-z][A-Za-z0-9-]*)\](.*)$/i;

interface InlineToken {
  kind: 'text' | 'wikilink' | 'embed' | 'tag';
  value: string;
  alias?: string;
}

function tokenizeInline(line: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  let cursor = 0;
  const matches: { start: number; end: number; token: InlineToken }[] = [];

  EMBED_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = EMBED_TOKEN.exec(line)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      token: { kind: 'embed', value: m[1]!.trim() },
    });
  }
  WIKILINK_TOKEN.lastIndex = 0;
  while ((m = WIKILINK_TOKEN.exec(line)) !== null) {
    // Skip if inside an embed range.
    const inside = matches.some(
      (r) => r.token.kind === 'embed' && m!.index >= r.start && m!.index < r.end,
    );
    if (inside) continue;
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      token: { kind: 'wikilink', value: m[1]!.trim(), alias: m[3]?.trim() },
    });
  }
  TAG_TOKEN.lastIndex = 0;
  while ((m = TAG_TOKEN.exec(line)) !== null) {
    // The leading whitespace or BOL is part of group 0 not group 1.
    const tagStart = m.index + m[0].indexOf('#');
    const inside = matches.some(
      (r) => tagStart >= r.start && tagStart < r.end,
    );
    if (inside) continue;
    matches.push({
      start: tagStart,
      end: tagStart + m[1]!.length + 1,
      token: { kind: 'tag', value: m[1]! },
    });
  }
  matches.sort((a, b) => a.start - b.start);

  for (const m of matches) {
    if (cursor < m.start) {
      tokens.push({ kind: 'text', value: line.slice(cursor, m.start) });
    }
    tokens.push(m.token);
    cursor = m.end;
  }
  if (cursor < line.length) {
    tokens.push({ kind: 'text', value: line.slice(cursor) });
  }
  if (tokens.length === 0) tokens.push({ kind: 'text', value: line });
  return tokens;
}

interface Block {
  kind: 'heading' | 'paragraph' | 'callout' | 'code' | 'list' | 'rule';
  level?: number;
  calloutKind?: string;
  calloutTitle?: string;
  lines: string[];
}

function parseBlocks(body: string): Block[] {
  const lines = body.split(/\r?\n/);
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (line.startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith('```')) {
        code.push(lines[i]!);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: 'code', lines: code });
      continue;
    }
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      blocks.push({
        kind: 'heading',
        level: headingMatch[1]!.length,
        lines: [headingMatch[2]!],
      });
      i++;
      continue;
    }
    if (/^---+\s*$/.test(line)) {
      blocks.push({ kind: 'rule', lines: [] });
      i++;
      continue;
    }
    const calloutMatch = line.match(CALLOUT_START);
    if (calloutMatch) {
      const calloutKind = calloutMatch[1]!.toLowerCase();
      const calloutTitle = calloutMatch[2]!.trim();
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i]!.startsWith('>')) {
        body.push(lines[i]!.replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'callout', calloutKind, calloutTitle, lines: body });
      continue;
    }
    if (/^[-*+]\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (
        i < lines.length &&
        (/^[-*+]\s+/.test(lines[i]!) || /^\d+\.\s+/.test(lines[i]!))
      ) {
        items.push(lines[i]!.replace(/^([-*+]|\d+\.)\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'list', lines: items });
      continue;
    }
    // Paragraph: collect until blank line, fence, heading, or callout.
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i]!.trim() !== '' &&
      !lines[i]!.startsWith('```') &&
      !/^#{1,6}\s+/.test(lines[i]!) &&
      !CALLOUT_START.test(lines[i]!) &&
      !/^---+\s*$/.test(lines[i]!) &&
      !/^[-*+]\s+/.test(lines[i]!) &&
      !/^\d+\.\s+/.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i++;
    }
    blocks.push({ kind: 'paragraph', lines: para });
  }
  return blocks;
}

const CALLOUT_COLORS: Record<string, { bg: string; border: string }> = {
  note: { bg: '#eef3ff', border: '#5b6ee3' },
  info: { bg: '#eef3ff', border: '#5b6ee3' },
  warning: { bg: '#fff5d6', border: '#c79a14' },
  example: { bg: '#e9fbe9', border: '#3a8a3a' },
  quote: { bg: '#f3eefa', border: '#8a5bc4' },
  todo: { bg: '#fff5d6', border: '#c79a14' },
  danger: { bg: '#fde6e6', border: '#c43a3a' },
};

function renderInline(
  tokens: InlineToken[],
  ctx: VaultEditorPaneProps,
  keyPrefix: string,
) {
  return tokens.map((tok, idx) => {
    const key = `${keyPrefix}-${idx}`;
    if (tok.kind === 'text') return <Fragment key={key}>{tok.value}</Fragment>;
    if (tok.kind === 'tag') return (
      <span key={key} className="vault-tag">#{tok.value}</span>
    );
    if (tok.kind === 'embed') return (
      <span
        key={key}
        style={{
          display: 'inline-block',
          padding: '0 4px',
          margin: '0 2px',
          border: '1px dashed #808080',
          background: '#f7f7f7',
          color: '#404040',
        }}
        title={`Embed: ${tok.value}`}
      >
        ![[{tok.value}]]
      </span>
    );
    // wikilink
    const target = tok.value;
    const display = tok.alias ?? target;
    const resolved =
      ctx.notesByPath.get(target) ??
      ctx.notesByPath.get(`${target}.md`) ??
      ctx.notesByName.get(target);
    return (
      <span
        key={key}
        className="vault-wikilink"
        style={{ opacity: resolved ? 1 : 0.5 }}
        title={resolved ? resolved.path : `Unresolved: ${target}`}
        onClick={(e) => {
          e.preventDefault();
          if (!resolved) return;
          ctx.onWikilink(
            resolved.path,
            e.metaKey || e.ctrlKey || e.shiftKey || ctx.paneId === 'a' ? 'pane-b' : 'pane-a',
          );
        }}
      >
        {display}
      </span>
    );
  });
}

function FrontmatterPanel({ note }: { note: ParsedNote }) {
  const entries = Object.entries(note.frontmatter);
  if (entries.length === 0) return null;
  return (
    <details
      open
      style={{
        marginBottom: 12,
        border: '1px solid #b0b0b0',
        background: '#fafafa',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          background: '#d9e8fa',
          fontWeight: 'bold',
        }}
      >
        Frontmatter
      </summary>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k}>
              <td
                style={{
                  padding: '2px 8px',
                  width: '30%',
                  fontWeight: 'bold',
                  verticalAlign: 'top',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                {k}
              </td>
              <td
                style={{
                  padding: '2px 8px',
                  borderTop: '1px solid #e0e0e0',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {JSON.stringify(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

export function VaultEditorPane(props: VaultEditorPaneProps) {
  const { note, paneId, editing, onLocalEdit, notesByPath, onWikilink } = props;
  const vaultNotes = useMemo(() => Array.from(notesByPath.values()), [notesByPath]);
  const blocks = useMemo(() => (note ? parseBlocks(note.body) : []), [note]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const testId =
    paneId === 'a'
      ? testIds.obsidianVault.editorPaneA
      : testIds.obsidianVault.editorPaneB;

  // C.14 — detect a Skills/<name>/SKILL.md note with valid frontmatter
  // so the editor can offer "Register as Skill". Only the active pane
  // shows the banner; the dialog itself is portal-rendered via fixed
  // position so it overlays the whole window.
  const skillCandidate = note ? matchSkillNote(note) : null;

  if (editing && note) {
    return (
      <div
        data-testid={testId}
        style={{
          height: '100%',
          overflowY: 'auto',
          background: 'white',
          borderRight: '1px solid #808080',
        }}
      >
        <div style={{ padding: 12, maxWidth: 760 }}>
          <h2 style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>
            {note.name}
          </h2>
          <div style={{ color: '#606060', fontSize: 11, marginBottom: 8 }}>
            {note.path} · editing
          </div>
          {skillCandidate && (
            <SkillBanner
              candidate={skillCandidate}
              onOpenDialog={() => setRegisterOpen(true)}
            />
          )}
          <FrontmatterPanel note={note} />
          <VaultTipTapEditor
            note={note}
            onChangeMarkdown={onLocalEdit}
            vaultNotes={vaultNotes}
            onOpenLink={onWikilink}
          />
        </div>
        {registerOpen && skillCandidate && skillCandidate.name && skillCandidate.description && (
          <RegisterAsSkillDialog
            vaultPath={skillCandidate.vaultPath}
            initialName={skillCandidate.name}
            initialDescription={skillCandidate.description}
            body={skillCandidate.body}
            onClose={() => setRegisterOpen(false)}
          />
        )}
      </div>
    );
  }

  if (!note) {
    return (
      <div
        data-testid={testId}
        style={{
          height: '100%',
          overflowY: 'auto',
          background: 'white',
          borderRight: '1px solid #808080',
        }}
      >
        <div className="vault-editor-empty">
          Pick a note from the tree to read it here. Wikilinks open in the
          other pane.
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      style={{
        height: '100%',
        overflowY: 'auto',
        background: 'white',
        borderRight: '1px solid #808080',
      }}
    >
      <div style={{ padding: 12, maxWidth: 760 }}>
        <h2
          style={{
            fontSize: 16,
            fontWeight: 'bold',
            marginBottom: 4,
            color: '#101010',
          }}
        >
          {note.name}
        </h2>
        <div style={{ color: '#606060', fontSize: 11, marginBottom: 8 }}>
          {note.path}
        </div>
        {skillCandidate && (
          <SkillBanner candidate={skillCandidate} onOpenDialog={() => setRegisterOpen(true)} />
        )}
        {registerOpen && skillCandidate && skillCandidate.name && skillCandidate.description && (
          <RegisterAsSkillDialog
            vaultPath={skillCandidate.vaultPath}
            initialName={skillCandidate.name}
            initialDescription={skillCandidate.description}
            body={skillCandidate.body}
            onClose={() => setRegisterOpen(false)}
          />
        )}
        <FrontmatterPanel note={note} />
        {blocks.map((b, i) => {
          if (b.kind === 'heading') {
            const Tag = (`h${Math.min(b.level ?? 1, 6)}` as keyof React.JSX.IntrinsicElements);
            return (
              <Tag
                key={i}
                style={{
                  fontWeight: 'bold',
                  marginTop: 8,
                  marginBottom: 4,
                  fontSize: [18, 16, 15, 14, 13, 13][Math.min((b.level ?? 1) - 1, 5)],
                }}
              >
                {renderInline(tokenizeInline(b.lines[0] ?? ''), props, `h${i}`)}
              </Tag>
            );
          }
          if (b.kind === 'rule') {
            return <hr key={i} style={{ borderTop: '1px solid #ece9d8', margin: '8px 0' }} />;
          }
          if (b.kind === 'paragraph') {
            return (
              <p key={i} style={{ marginBottom: 8 }}>
                {b.lines.map((ln, j) => (
                  <Fragment key={j}>
                    {renderInline(tokenizeInline(ln), props, `p${i}-${j}`)}
                    {j < b.lines.length - 1 && ' '}
                  </Fragment>
                ))}
              </p>
            );
          }
          if (b.kind === 'list') {
            return (
              <ul key={i} style={{ listStyle: 'disc', paddingLeft: 24, marginBottom: 8 }}>
                {b.lines.map((ln, j) => (
                  <li key={j}>{renderInline(tokenizeInline(ln), props, `li${i}-${j}`)}</li>
                ))}
              </ul>
            );
          }
          if (b.kind === 'code') {
            return (
              <pre
                key={i}
                style={{
                  background: '#f3f3f3',
                  border: '1px solid #d0d0d0',
                  padding: 8,
                  fontFamily: 'monospace',
                  fontSize: 11,
                  whiteSpace: 'pre-wrap',
                  marginBottom: 8,
                }}
              >
                {b.lines.join('\n')}
              </pre>
            );
          }
          if (b.kind === 'callout') {
            const colors = CALLOUT_COLORS[b.calloutKind ?? ''] ?? CALLOUT_COLORS.note!;
            return (
              <div
                key={i}
                style={{
                  background: colors.bg,
                  borderLeft: `4px solid ${colors.border}`,
                  padding: '6px 10px',
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 2 }}>
                  [{b.calloutKind}] {b.calloutTitle}
                </div>
                {b.lines.map((ln, j) => (
                  <div key={j}>
                    {renderInline(tokenizeInline(ln), props, `co${i}-${j}`)}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

function SkillBanner({
  candidate,
  onOpenDialog,
}: {
  candidate: SkillCandidate;
  onOpenDialog: () => void;
}) {
  const incomplete = !candidate.name || !candidate.description;
  return (
    <div
      data-testid={testIds.obsidianVault.registerSkillBanner}
      style={{
        background: incomplete ? '#fff7d6' : '#e6f3ff',
        border: `1px solid ${incomplete ? '#d8b300' : '#5b8dde'}`,
        padding: '6px 10px',
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        color: '#000',
      }}
    >
      <span style={{ flex: 1 }}>
        {incomplete
          ? `This looks like a SKILL.md but its frontmatter is missing name and/or description.`
          : `This is a SKILL.md note. Register it as a Skill to expose it to NIPE and Prompt Lab.`}
      </span>
      <button
        type="button"
        data-testid={testIds.obsidianVault.registerSkillButton}
        onClick={onOpenDialog}
        disabled={incomplete}
        style={{ padding: '2px 10px', cursor: incomplete ? 'not-allowed' : 'pointer' }}
      >
        Register as Skill
      </button>
    </div>
  );
}
